/**
 * @file
 * JavaScript for passkey authentication on login page.
 *
 * Preferred flow is conditional mediation ("passkey autofill"): a discovery
 * request sits pending from page load, and the browser offers stored passkeys
 * in the username field's autofill dropdown. The username field is tagged
 * autocomplete="username webauthn" by the wellknown module, which is the token
 * conditional UI keys on.
 *
 * An explicit "Sign in with a passkey" button is always offered as well, and
 * is the whole flow on browsers without conditional mediation. It ships even
 * when autofill is running because "mediation is available" does not mean the
 * dropdown will actually surface a passkey — see drupal-8j3. The modal is
 * never auto-fired; it opens only on a press.
 */

(function () {
  'use strict';

  // A conditional request can sit pending far longer than the challenge it
  // carries lives, so it is re-issued before that challenge goes stale — or
  // finish-login would reject a passkey the user successfully picked.
  //
  // How long it lives is the REGISTRAR's decision, and begin-login now reports
  // it as challengeTTLSeconds (drupal-tbm). This constant used to mirror
  // PasskeyService.challengeTTL with nothing linking the two: lowering it there
  // would have left the re-arm too late here, and every conditional login
  // attempted after the real expiry would fail with "Challenge not found" — a
  // silent auth break with no test on either side.
  //
  // Kept as the FALLBACK for a response that carries no TTL: the registrar may
  // not have been deployed yet, and an absent field must not become NaN and a
  // timer that fires immediately.
  var DEFAULT_CHALLENGE_TTL_MS = 5 * 60 * 1000;
  var REARM_MARGIN_MS = 30 * 1000;
  // Never schedule a re-arm sooner than this, however short a TTL we are told.
  // Below it the page would spend more time reissuing challenges than holding
  // one, which is worse than a slightly stale challenge.
  var MIN_REARM_DELAY_MS = 30 * 1000;
  // ...and never treat a TTL as longer than this. setTimeout stores its delay
  // in a signed 32-bit int, so anything above 2^31-1 ms (~24.8 days) WRAPS AND
  // IS CLAMPED TO 1 — an absurd TTL would therefore fire the re-arm
  // immediately and keep firing, the opposite of what it says. No real
  // challenge outlives an hour, so cap there rather than at the overflow
  // boundary; being early costs one extra begin-login, being wrong costs a
  // loop.
  var MAX_CHALLENGE_TTL_MS = 60 * 60 * 1000;
  // Both TTL complaints are latched once per page, like rpIdReported below and
  // for the same reason: they describe a standing disagreement between this
  // client and the registrar, not an event. A 10s TTL re-arms every 30s, so an
  // unlatched warning would repeat ~120 times an hour and bury the console.
  var ttlComplaintReported = false;

  // A conditional request that fails is restarted, so cap the restarts: an
  // error that reproduces instantly (an authenticator that rejects every
  // discovery request) would otherwise spin begin-login forever.
  var MAX_CONDITIONAL_RESTARTS = 3;

  // How long a request must have been pending for its ending to count as
  // ordinary rather than as an instant failure. See restartConditionalLogin().
  var MIN_HEALTHY_PENDING_MS = 1000;

  // Backstop on restarts of every kind. Ordinary dismissals are not capped by
  // MAX_CONDITIONAL_RESTARTS, so without this a browser that rejected just
  // past MIN_HEALTHY_PENDING_MS every time would issue begin-login forever.
  // Set far above any plausible amount of real interaction on one page.
  var MAX_CONDITIONAL_ATTEMPTS = 50;

  // Non-null while a conditional request is pending; aborting it is how we
  // free up the one WebAuthn request a browser allows at a time.
  var conditionalController = null;
  // True from the moment we ask for a challenge until the request is pending
  // or has given up. conditionalController alone can't cover that window: it
  // is null while begin-login is in flight, which would let a second caller
  // start a duplicate request.
  var conditionalStarting = false;
  // Bumped by releaseConditional(). Each attempt captures the value at its
  // start and abandons itself if it no longer matches, which is what lets a
  // caller stand an attempt down while its begin-login is still in flight —
  // there is no AbortController yet at that point, so without this a fetch
  // frozen by the back-forward cache and never settled would hold
  // conditionalStarting true forever and block every later attempt.
  var conditionalGeneration = 0;
  var conditionalRestarts = 0;
  var conditionalAttempts = 0;
  var conditionalPendingSince = 0;
  // Set once a conditional request has actually gone pending, which is the
  // only proof we get that begin-login is working on this page. Distinguishes
  // "it has failed from the start" from "it just blipped" on a later failure.
  var conditionalEverPended = false;
  var challengeIssuedAt = 0;
  // The TTL of the challenge currently pending, from the response that carried
  // it. Per-attempt rather than a constant, because it is the server's answer
  // about THIS challenge — see ttlFromResponse().
  var challengeTtlMs = DEFAULT_CHALLENGE_TTL_MS;
  var rearmTimer = null;
  var modalInProgress = false;
  // True across a finish-login round trip, on either flow. loginSucceeded only
  // flips after finish-login returns, so without this the other flow's guard
  // would wave a request through while an assertion is mid-verification and
  // open a second WebAuthn prompt.
  var finishInProgress = false;
  var loginSucceeded = false;
  var lifecycleBound = false;
  // Every "Sign in with a passkey" button on the page. Module scope, not per
  // container: only one WebAuthn request may be live, so they are disabled and
  // re-enabled as a set. See setButtonsDisabled().
  var modalButtons = [];

  // Handed between beginLogin()'s two stages to mean "this failure has already
  // been named on the console". A distinct object rather than null, because a
  // server can legitimately send a literal JSON `null` body and response.json()
  // resolves that to null too — see the second stage.
  var BEGIN_LOGIN_REPORTED = {};

  // Drupal 6 behaviors: behaviors are functions, not objects with attach methods
  Drupal.behaviors.passkeyLogin = function (context) {
    context = queryableContext(context);

    if (!window.PublicKeyCredential) {
      console.log('Passkey: WebAuthn not supported in this browser');
      return;
    }

    // The container rides on the login form (see passkey_form_alter), so its
    // presence is the page gate: no form, nothing to do.
    //
    // querySelectorAll, not querySelector: the container is emitted as raw
    // markup, so form_clean_id() never runs on it, and a page rendering the
    // login block alongside the login form carries TWO elements with this id.
    // Taking only the first would hand the button to whichever the theme put
    // first in the DOM and leave the other login form with nothing — and would
    // decide, by DOM order, whether autofill arms at all (drupal-33k).
    var containers = context.querySelectorAll('#passkey-login-area');
    if (!containers.length) {
      return;
    }

    for (var i = 0; i < containers.length; i++) {
      initContainer(containers[i]);
    }
  };

  /**
   * Set up one login container: the button always, autofill if this form asked
   * for it.
   *
   * The processed marker is per element, so an AJAX-injected second login form
   * gets its own setup rather than being skipped.
   */
  function initContainer(container) {
    if (container.classList.contains('passkey-processed')) {
      return;
    }
    container.classList.add('passkey-processed');

    // Only the login PAGE pre-arms conditional mediation; the login block does
    // not, because it can appear on every page and would then spend a
    // begin-login and a challenge row on every anonymous page view
    // (drupal-33k). passkey_form_alter() sets the attribute.
    var wantsAutofill = container.getAttribute('data-passkey-autofill') === '1';

    var buttonAdded = false;

    supportsConditionalMediation().then(function (available) {
      // The button is offered unconditionally (drupal-8j3). Conditional
      // mediation being *available* is not a promise that the dropdown will
      // ever surface a passkey: in-app webviews (Facebook, Instagram, Gmail)
      // report support without implementing the autofill surface, and a
      // password manager extension can own the username field's dropdown.
      // Offering the button only in the else branch left those users with no
      // passkey affordance at all — no autofill and no button — where before
      // this flow existed the modal always fired.
      //
      // logintoboggan_login_block_type=2 is NOT one of the cases this helps.
      // _logintoboggan_toggleboggan() wraps the whole form in #toboggan-login
      // via pre/post elements at weights ±300, and this container sits at
      // weight -10 inside that wrapper — so the button starts hidden exactly
      // as the username field does, and both become visible together when the
      // user clicks the toggle. Nothing here changes that.
      //
      // It costs nothing on the autofill path: the button is inert until
      // pressed, so no modal opens on page load, and startModalLogin() stands
      // the pending conditional request down before opening its own.
      addModalButton(container);
      buttonAdded = true;

      // Conditional mediation also needs a field the browser will hang
      // autofill on; without the token the request would sit pending and
      // invisible.
      if (!wantsAutofill) {
        console.log('Passkey: this form does not pre-arm autofill, the button is its path');
      } else if (available && hasWebauthnAutofillField()) {
        console.log('Passkey: using conditional mediation (autofill), with the button as a backstop');
        bindLifecycleListeners();
        startConditionalLogin();
      } else {
        console.log('Passkey: conditional mediation unavailable, the button is the only path');
      }
    }).catch(function (error) {
      // The container is already marked processed, so without this a throw in
      // the callback above would leave the page with no passkey affordance at
      // all and nothing in the console but an unhandled rejection.
      console.error('Passkey: could not initialise passkey login:', error);
      if (buttonAdded) {
        // The affordance is already on the page — the throw was in the
        // conditional setup underneath it — so there is nothing to add, and
        // adding anyway would give this container a second button.
        return;
      }
      // Otherwise the throw came from addModalButton() itself or earlier. Try
      // once more in isolation: it needs only createElement and a click
      // listener, and nothing reachable above has opened a WebAuthn request
      // for it to collide with.
      try {
        addModalButton(container);
      } catch (buttonError) {
        // Even the fallback failed, so this container is genuinely unusable.
        // Drop the processed marker rather than leaving it inert for the life
        // of the page: a later behavior pass (an AJAX-injected form, a retried
        // render) then gets one more attempt.
        console.error('Passkey: fallback button could not be added either:', buttonError);
        container.classList.remove('passkey-processed');
      }
    });
  }

  /**
   * Turn whatever Drupal handed us into something we can call querySelectorAll
   * on.
   *
   * A Drupal 6 behavior receives whatever its caller happened to have, and the
   * callers on this site do not agree: core's misc/ahah.js and ctools'
   * js/ajax-responder.js pass jQuery objects, and views' js/ajax.js passes a
   * raw selector STRING in two places. None of those has querySelectorAll.
   *
   * That matters more than a failed lookup would, because
   * Drupal.attachBehaviors() (misc/drupal.js) runs behaviors inside
   * jQuery.each with no try/catch: a throw here aborts every behavior
   * registered after this one for that context. An AJAX pager would take out
   * unrelated JavaScript on the page. rhodes.hue-picker.js carries a warning
   * about the same trap.
   *
   * Falling back to document is safe rather than merely convenient. The
   * passkey-processed marker is per element, so re-scanning the whole page
   * costs one no-op per container already set up — and it happens to fix the
   * companion limitation, that an element context only matches its
   * DESCENDANTS, so content injected as the container itself would otherwise
   * be missed.
   */
  function queryableContext(context) {
    if (context && typeof context.querySelectorAll === 'function') {
      return context;
    }
    return document;
  }

  /**
   * Is a field tagged for passkey autofill present?
   *
   * The wellknown module puts autocomplete="username webauthn" on the login
   * username field, and conditional UI keys on that token. passkey does not
   * work without it, which is why the .info declares the dependency — but the
   * modules can still come apart in a live database (a snapshot restored from
   * an environment where wellknown was never enabled will do it), so check the
   * page rather than trust the configuration.
   */
  function hasWebauthnAutofillField() {
    return !!document.querySelector('input[autocomplete~="webauthn"]');
  }

  function supportsConditionalMediation() {
    if (typeof PublicKeyCredential.isConditionalMediationAvailable !== 'function') {
      return Promise.resolve(false);
    }
    return PublicKeyCredential.isConditionalMediationAvailable().catch(function () {
      return false;
    });
  }

  /**
   * Ask the server for a challenge and request options.
   *
   * Always discovery mode. passkey_begin_login() forwards drupalUID => NULL
   * unconditionally and ignores the request body (drupal-ybj: honoring a
   * caller-supplied UID would hand an anonymous caller that account's real
   * credential IDs), and the registrar's beginAuthentication() never builds
   * allowCredentials. The body is still a JSON object because the endpoint
   * rejects a body it cannot decode.
   *
   * Resolves with {publicKey, challenge}, or null when it failed.
   *
   * A null NEVER means "this account has no passkey". Discovery mode does not
   * consult anyone's credentials — the registrar's beginAuthentication() mints
   * a challenge and builds no allowCredentials — so the only way to get here
   * empty-handed is a failure on our side of the ceremony: the registrar down,
   * curl timing out inside passkey_make_request(), a missing
   * PASSKEY_SHARED_SECRET. Callers must report it that way (drupal-3nn); the
   * distinguishing detail is logged here so one console line names the cause.
   *
   * "This account has no passkey" surfaces later and elsewhere, when
   * navigator.credentials.get() finds nothing to offer.
   */
  function beginLogin() {
    return fetch('/passkey/begin-login', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ drupalUID: null })
    })
      .then(function (response) {
        var contentType = response.headers.get('content-type') || '';
        if (!response.ok) {
          console.error('Passkey: begin-login failed with HTTP ' + response.status +
            ' — passkey sign-in is unavailable right now');
          return BEGIN_LOGIN_REPORTED;
        }
        if (!contentType.includes('application/json')) {
          console.error('Passkey: begin-login answered with ' +
            (contentType || 'no content type') + ' rather than JSON — ' +
            'passkey sign-in is unavailable right now');
          return BEGIN_LOGIN_REPORTED;
        }
        return response.json();
      })
      .then(function (response) {
        // The sentinel, not null: response.json() resolves to null for a
        // literal JSON `null` body, so keying "already reported" off null would
        // swallow that failure unlogged — the very hole this reporting exists
        // to close.
        if (response === BEGIN_LOGIN_REPORTED) {
          return null;
        }
        if (response && response.publicKey && response.challenge) {
          return response;
        }
        console.error('Passkey: begin-login returned no challenge (' +
          ((response && response.error) || 'no reason given') +
          ') — passkey sign-in is unavailable right now');
        return null;
      })
      .catch(function (error) {
        console.error('Passkey: begin-login request failed — ' +
          'passkey sign-in is unavailable right now:', error);
        return null;
      });
  }

  /**
   * Build the options for navigator.credentials.get().
   *
   * Conditional mediation requires discovery mode, so allowCredentials is
   * dropped rather than passed through.
   */
  function buildRequestOptions(publicKeyOptions, discoveryOnly) {
    var allowCredentials;
    if (!discoveryOnly && publicKeyOptions.allowCredentials) {
      allowCredentials = publicKeyOptions.allowCredentials.map(function (cred) {
        return {
          id: base64UrlToArrayBuffer(cred.id),
          type: 'public-key',
          transports: cred.transports || []
        };
      });
    }

    return {
      challenge: base64UrlToArrayBuffer(publicKeyOptions.challenge),
      timeout: publicKeyOptions.timeout || 60000,
      rpId: publicKeyOptions.rpId,
      allowCredentials: allowCredentials,
      userVerification: publicKeyOptions.userVerification || 'preferred'
    };
  }

  // MARK: - Conditional (autofill) flow

  function startConditionalLogin() {
    if (loginSucceeded || finishInProgress || modalInProgress || conditionalController || conditionalStarting) {
      return;
    }
    conditionalStarting = true;
    var generation = conditionalGeneration;

    beginLogin().then(function (response) {
      // Superseded while begin-login was in flight — see conditionalGeneration.
      // Whoever released us has already started a replacement, so returning
      // here is the whole point: clearing conditionalStarting or installing a
      // controller now would stamp on state the newer attempt owns.
      if (generation !== conditionalGeneration) {
        return;
      }
      conditionalStarting = false;
      if (loginSucceeded || finishInProgress || modalInProgress || conditionalController) {
        return;
      }
      if (!response) {
        // beginLogin() has logged why. A null is always a failure on our side
        // (see its docblock), never an answer about this user's credentials.
        //
        // On the first attempt, stop: if begin-login is failing at page load
        // the registrar is down or misconfigured, and hammering it once a
        // second would not help. The user is not stranded — the modal button
        // is on the page either way (drupal-8j3), and pressing it reports the
        // outage rather than pretending they have no passkey.
        //
        // Once a request HAS been pending, a null is a blip against a server
        // we know was working seconds ago, so retry: otherwise one failed
        // re-arm would end autofill for the life of the page.
        if (conditionalEverPended) {
          console.log('Passkey: re-arm could not get a challenge, retrying');
          restartConditionalLogin(0);
        }
        return;
      }

      var controller = new AbortController();
      conditionalController = controller;
      challengeIssuedAt = Date.now();
      conditionalPendingSince = challengeIssuedAt;
      challengeTtlMs = ttlFromResponse(response);
      scheduleRearm();

      // buildRequestOptions() is evaluated as the argument here, so a malformed
      // challenge throws before the request exists — hence set conditionalEverPended
      // only once get() has actually been called. Getting this wrong made a
      // never-pended attempt claim the server was healthy, so a later failure
      // looked like a blip worth retrying and burned the restart budget.
      var getPromise = navigator.credentials.get({
        publicKey: buildRequestOptions(response.publicKey, true),
        mediation: 'conditional',
        signal: controller.signal
      });
      conditionalEverPended = true;

      getPromise.then(function (credential) {
        conditionalController = null;
        clearRearm();
        // The button ships alongside autofill now (drupal-8j3), so a press can
        // land in the gap between this promise settling and this handler
        // running — releaseConditional()'s abort does not reach a promise that
        // has already resolved. Both flows would then call finishLogin() and
        // run a finish-login round trip each. Yield to the one the user is
        // looking at: the modal is about to prompt them anyway, and the
        // registrar treats each challenge as single-use, so racing it gains
        // nothing and costs a spurious failure alert.
        if (loginSucceeded || finishInProgress || modalInProgress) {
          console.log('Passkey: a conditional credential arrived while the modal flow had the floor, dropping it');
          return;
        }
        finishLogin(credential, response.challenge, function () {
          // The attempt consumed the pending request; put a fresh one in
          // place rather than leaving autofill dead until reload. The user had
          // to pick a passkey to get here, so this is never a tight loop.
          restartConditionalLogin(Date.now() - conditionalPendingSince);
        });
      }).catch(function (error) {
        if (conditionalController === controller) {
          conditionalController = null;
          clearRearm();
        }
        // Our own abort means a replacement is already on its way. A browser
        // abort does not — freezing the page for the back-forward cache can
        // cancel a pending request — so that one still needs a restart, or
        // autofill would be dead on restore.
        if (error.name === 'AbortError' && controller.passkeyAbortedByUs) {
          return;
        }
        // Anything else ends the pending request for good: some browsers
        // reject when the user dismisses the dropdown, and one that honored
        // the timeout we pass would reject there too. Without a restart,
        // autofill would silently never come back.
        console.log('Passkey: conditional request ended (' + error.name + '), restarting');
        reportRpIdMismatch(error, response.publicKey);
        restartConditionalLogin(Date.now() - conditionalPendingSince);
      });
    }).catch(function (error) {
      // A superseded attempt keeps its failure to itself: releasing here would
      // tear down the replacement's request, and restarting would spend a cap
      // slot on an attempt nobody is waiting for.
      if (generation !== conditionalGeneration) {
        console.log('Passkey: superseded conditional attempt failed (' + error.name + '), ignoring');
        return;
      }
      // The inner catch above only covers credentials.get()'s promise, and
      // buildRequestOptions() is evaluated as its argument — so a throw there
      // (atob() on a malformed challenge, credentials.get() rejecting bad
      // option types synchronously) lands here instead. Without this, the
      // throw escapes as an unhandled rejection and leaves conditionalController
      // set, which blocks every future attempt: autofill dies silently.
      releaseConditional();
      console.error('Passkey: conditional login failed to start:', error);
      // The request never went pending, so this counts against the cap: a
      // throw here will recur the moment we retry.
      restartConditionalLogin(0);
    });
  }

  /**
   * Name the rpId and the origin when the browser rejects on a security check.
   *
   * A SecurityError from credentials.get() nearly always means the rpId the
   * registrar issued is not a registrable suffix of the page's origin. That is
   * a live class of failure here rather than a theoretical one: this site is
   * served as edgewoodsailing.org, dev.* and test.*, and the registrar's
   * WEBAUTHN_RP_ID differs per environment (with a fallback to the production
   * value). Without the two values printed, the symptom is three rounds of
   * "conditional request ended (SecurityError), restarting" and then silence.
   *
   * Logged once per page: this is diagnosis for whoever opens the console, and
   * the restart loop would otherwise repeat it verbatim.
   */
  var rpIdReported = false;
  function reportRpIdMismatch(error, publicKeyOptions) {
    if (rpIdReported || !error || error.name !== 'SecurityError') {
      return;
    }
    rpIdReported = true;
    console.error('Passkey: the browser refused the request on a security check. ' +
      'rpId=' + ((publicKeyOptions && publicKeyOptions.rpId) || '(none sent)') +
      ' origin=' + window.location.origin +
      ' hostname=' + window.location.hostname +
      '. The rpId must be the page hostname or a registrable parent of it; ' +
      'check the registrar WEBAUTHN_RP_ID for this environment.');
  }

  /**
   * Drop any pending conditional request and its re-arm timer.
   */
  function releaseConditional() {
    conditionalStarting = false;
    // Disown any begin-login still in flight. Clearing conditionalStarting
    // alone is not enough: that fetch's handler would still run and install a
    // controller behind the replacement's back.
    conditionalGeneration++;
    if (conditionalController) {
      var controller = conditionalController;
      conditionalController = null;
      // Marked on the controller rather than a shared flag so the rejection
      // handler, which closes over this exact controller, cannot mistake a
      // browser abort of a later request for this deliberate one.
      controller.passkeyAbortedByUs = true;
      controller.abort();
    }
    clearRearm();
  }

  /**
   * Replace a conditional request that has ended.
   *
   * Only an instant failure counts against the cap, because that is the
   * runaway the cap exists for: a malformed challenge or a rejected rpId will
   * fail again the moment we retry. A request that stayed pending and then
   * ended is ordinary — most often the user dismissing the autofill dropdown,
   * which some browsers report as a rejection. Counting those would let three
   * taps in and out of the username field disable autofill for the rest of
   * the page.
   */
  function restartConditionalLogin(pendingMs) {
    conditionalAttempts++;
    if (conditionalAttempts > MAX_CONDITIONAL_ATTEMPTS) {
      console.log('Passkey: conditional attempt limit reached, autofill is done for this page');
      return;
    }
    if (pendingMs < MIN_HEALTHY_PENDING_MS) {
      if (conditionalRestarts >= MAX_CONDITIONAL_RESTARTS) {
        console.log('Passkey: conditional restart limit reached, autofill is done for this page');
        return;
      }
      conditionalRestarts++;
    } else {
      // A request that stayed healthily pending proves the instant-failure mode
      // cleared, so forgive the earlier ones: a transient blip minutes ago must
      // not leave a genuinely recoverable failure later with no retries.
      conditionalRestarts = 0;
    }
    startConditionalLogin();
  }

  /**
   * Report a TTL disagreement, at most once for the life of the page.
   */
  function complainAboutTtl(message) {
    if (ttlComplaintReported) {
      return;
    }
    ttlComplaintReported = true;
    console.warn(message);
  }

  /**
   * How long the challenge in this begin-login response is good for.
   *
   * The registrar reports challengeTTLSeconds (drupal-tbm). Validate it hard:
   * this number decides a timer, so a string, a negative, a zero or an Infinity
   * would either fire immediately in a loop or never fire at all — and the
   * second is the silent auth break the field exists to prevent, arrived at
   * from the other direction.
   */
  function ttlFromResponse(response) {
    var seconds = response && response.challengeTTLSeconds;
    if (typeof seconds !== 'number' || !isFinite(seconds) || seconds <= 0) {
      return DEFAULT_CHALLENGE_TTL_MS;
    }
    if (seconds * 1000 > MAX_CHALLENGE_TTL_MS) {
      complainAboutTtl('Passkey: ignoring an implausible challenge TTL of ' + seconds +
        's; holding for at most ' + (MAX_CHALLENGE_TTL_MS / 1000) + 's');
      return MAX_CHALLENGE_TTL_MS;
    }
    return seconds * 1000;
  }

  /**
   * When to swap the pending challenge for a fresh one: REARM_MARGIN_MS before
   * it expires, but never sooner than MIN_REARM_DELAY_MS.
   *
   * The clamp matters because the margin is ours and the TTL is the server's.
   * If they ever cross — a TTL at or under 30s — the delay goes zero or
   * negative and the page reissues begin-login as fast as the event loop
   * allows. Better to hold a challenge slightly past our own comfort margin.
   */
  function rearmDelay() {
    return Math.max(challengeTtlMs - REARM_MARGIN_MS, MIN_REARM_DELAY_MS);
  }

  function scheduleRearm() {
    clearRearm();
    if (challengeTtlMs - REARM_MARGIN_MS < MIN_REARM_DELAY_MS) {
      // Worth saying out loud: it means the registrar's TTL and this client's
      // margin genuinely disagree about what is workable.
      complainAboutTtl('Passkey: challenge TTL of ' + (challengeTtlMs / 1000) +
        's leaves no room for the ' + (REARM_MARGIN_MS / 1000) +
        's re-arm margin; holding for ' + (rearmDelay() / 1000) + 's instead');
    }
    rearmTimer = setTimeout(function () {
      rearmConditionalLogin('challenge nearing expiry');
    }, rearmDelay());
  }

  function clearRearm() {
    if (rearmTimer) {
      clearTimeout(rearmTimer);
      rearmTimer = null;
    }
  }

  /**
   * Swap the pending conditional request for one carrying a fresh challenge.
   *
   * The abort closes the autofill dropdown if it happens to be open; the new
   * request is issued immediately, so refocusing the field brings it back.
   * That beats the alternative, where picking a passkey fails at finish-login.
   */
  function rearmConditionalLogin(reason) {
    if (!conditionalController) {
      return;
    }
    console.log('Passkey: re-arming conditional request (' + reason + ')');
    releaseConditional();
    // Not restartConditionalLogin(): a re-arm is routine upkeep, not a
    // failure, so it must not count against the restart cap.
    startConditionalLogin();
  }

  /**
   * Watch for the page coming back after being away.
   *
   * Bound once, and only on the conditional path — there is nothing to keep
   * fresh when the modal button is the flow, and the module scope is shared
   * across behavior re-runs on AJAX-injected forms.
   */
  function bindLifecycleListeners() {
    if (lifecycleBound) {
      return;
    }
    lifecycleBound = true;

    // Background tabs throttle timers, so a challenge can outlive its re-arm.
    document.addEventListener('visibilitychange', function () {
      if (document.hidden || !conditionalController) {
        return;
      }
      if (Date.now() - challengeIssuedAt >= rearmDelay()) {
        rearmConditionalLogin('page restored with a stale challenge');
      }
    });

    // A back-forward cache restore can hand back a page whose pending request
    // the browser cancelled on freeze. When it cancelled with a rejection the
    // handler above has already restarted; when it discarded the request
    // silently, nothing has, and conditionalController is stale or null — so
    // rebuild from scratch rather than inspecting what survived.
    //
    // Deliberately NOT gated on conditionalStarting. A begin-login frozen
    // mid-flight and never settled is exactly the state that needs healing,
    // and it is indistinguishable from one that started microseconds ago;
    // releaseConditional() bumps the generation, so the rebuild supersedes
    // either safely and the worst case is one wasted begin-login per restore.
    window.addEventListener('pageshow', function (event) {
      if (!event.persisted || loginSucceeded || modalInProgress) {
        return;
      }
      console.log('Passkey: restored from bfcache, reissuing conditional request');
      releaseConditional();
      startConditionalLogin();
    });
  }

  // MARK: - Modal flow (fallback)

  function addModalButton(container) {
    var button = document.createElement('button');
    button.type = 'button';
    button.className = 'passkey-login-button';
    button.textContent = Drupal.t('Sign in with a passkey');
    button.addEventListener('click', function () {
      startModalLogin();
    });
    container.appendChild(button);
    modalButtons.push(button);
  }

  /**
   * Disable or re-enable every passkey button on the page at once.
   *
   * A page can carry more than one since drupal-33k — the login block renders
   * its own container alongside the login form's — and the browser allows only
   * one WebAuthn request at a time. Disabling only the button that was pressed
   * would leave the others looking live while startModalLogin() turns them
   * away at its guard, which is the silent-no-op-button failure again.
   */
  function setButtonsDisabled(disabled) {
    for (var i = 0; i < modalButtons.length; i++) {
      modalButtons[i].disabled = disabled;
    }
  }

  /**
   * Start a modal passkey login from the button.
   *
   * Since drupal-8j3 the button is present even when conditional mediation is
   * running, so a pending conditional request is the ORDINARY state here, not
   * the edge case it used to be — the user pressing the button is usually a
   * user telling us autofill did not work for them.
   */
  function startModalLogin() {
    if (loginSucceeded || finishInProgress || modalInProgress) {
      // Reachable by pressing a second button, or the same one twice before
      // the buttons are disabled below. Say so rather than doing nothing
      // visible and nothing loggable.
      console.log('Passkey: a passkey sign-in is already in progress, ignoring this press');
      return;
    }
    // Browsers allow one WebAuthn request at a time, so the conditional
    // request has to stand down before this one opens, or the click would be
    // rejected with NotAllowedError — which the handler below suppresses as a
    // user cancellation, so the button would appear to do nothing at all.
    releaseConditional();
    modalInProgress = true;
    setButtonsDisabled(true);

    var release = function () {
      modalInProgress = false;
      setButtonsDisabled(false);
      // releaseConditional() above tore down the pending conditional request.
      // Now that the modal attempt is over, bring autofill back, or it would
      // stay dead for the life of the page — which matters more since the
      // button ships alongside autofill (drupal-8j3): a user who presses it,
      // changes their mind, and then reaches for the username field must
      // still get the dropdown. lifecycleBound is only set on the conditional
      // path, so this is a no-op on a page where the button is the only flow.
      // A healthy pendingMs marks this as routine restoration, not an instant
      // failure.
      if (lifecycleBound) {
        restartConditionalLogin(MIN_HEALTHY_PENDING_MS);
      }
    };

    beginLogin().then(function (response) {
      if (!response) {
        release();
        // NOT "you have no passkey": begin-login cannot report that (see
        // beginLogin()). Every null here is our own outage, and telling a user
        // with a working passkey that they do not have one sends them off to
        // re-register it during an incident (drupal-3nn).
        alert(Drupal.t('Passkey sign-in is temporarily unavailable. Please use your username and password.'));
        return;
      }

      navigator.credentials.get({
        publicKey: buildRequestOptions(response.publicKey, false)
      }).then(function (credential) {
        finishLogin(credential, response.challenge, release);
      }).catch(function (error) {
        release();
        reportRpIdMismatch(error, response.publicKey);
        // Name it on the console even when the dialog is suppressed. The button
        // is the primary affordance on conditional-capable browsers now
        // (drupal-8j3), and the suppressed names cover more than a user
        // cancelling: NotAllowedError is also what WebKit returns when it
        // refuses a credentials.get() it no longer considers inside the user
        // activation window — which this code can hit, because the click
        // handler awaits begin-login before calling get(). Those two are
        // indistinguishable to us, but a button that does literally nothing
        // with an empty console is undiagnosable. See drupal-pqn.
        console.log('Passkey: modal request ended (' + error.name + '): ' + error.message);
        // The user cancelling their own click needs no dialog.
        if (error.name !== 'NotAllowedError' && error.name !== 'AbortError' && error.name !== 'NotSupportedError') {
          alert(Drupal.t('Passkey authentication error: !message', { '!message': error.message }));
        }
      });
    }).catch(function (error) {
      // As in the conditional flow, a throw inside the callback above skips
      // the inner catch (buildRequestOptions() is an argument to
      // credentials.get(), not part of its promise). Without this the button
      // would stay disabled for good, with no timer to heal it.
      release();
      console.error('Passkey: modal login failed to start:', error);
      alert(Drupal.t('Passkey authentication error: !message', { '!message': error.message }));
    });
  }

  // MARK: - Shared completion

  /**
   * Trade the signed assertion for a Drupal session.
   *
   * A failure here means the user picked a passkey and it did not work, so it
   * is reported even on the conditional path — unlike the silent fallbacks
   * above, this one they were expecting to log them in. onFailure lets the
   * caller put its own flow back in a usable state; without it a failed
   * attempt would strand the user on a page whose passkey login is dead.
   */
  function finishLogin(credential, storedChallenge, onFailure) {
    // Hold both flows off until this settles — see finishInProgress. Cleared
    // only on failure; success navigates away, so there is nothing to release.
    finishInProgress = true;

    // swift-webauthn expects the standard WebAuthn structure:
    // - id (string, base64url)
    // - rawId (base64url encoded ArrayBuffer)
    // - type (string)
    // - response (object with clientDataJSON, authenticatorData, signature, userHandle)
    var authenticationCredential = {
      id: credential.id,
      type: credential.type,
      rawId: arrayBufferToBase64Url(credential.rawId),
      response: {
        clientDataJSON: arrayBufferToBase64Url(credential.response.clientDataJSON),
        authenticatorData: arrayBufferToBase64Url(credential.response.authenticatorData),
        signature: arrayBufferToBase64Url(credential.response.signature),
        userHandle: credential.response.userHandle ? arrayBufferToBase64Url(credential.response.userHandle) : null
      }
    };

    fetch('/passkey/finish-login', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        challengeBase64: storedChallenge,
        credentialJSON: JSON.stringify(authenticationCredential),
        // Forwarded RAW, exactly as it appeared on the login page's URL
        // (drupal-bi5). Deliberately not pre-validated here: the server must
        // not trust it either way, and sending an already-narrowed value would
        // hide from the server what the user actually asked for. The server
        // allowlists it, and redirectTarget() below still checks whatever comes
        // back — so this is the second of two independent checks, not a
        // replacement for either.
        destination: new URLSearchParams(window.location.search).get('destination')
      })
    })
      .then(function (response) {
        var contentType = response.headers.get('content-type') || '';
        if (!response.ok) {
          return response.text().then(function (text) {
            var message = 'HTTP error! status: ' + response.status;
            try {
              var err = JSON.parse(text);
              message = err.error || message;
            } catch (e) {
              // Non-JSON error body; the status line is all we have.
            }
            throw new Error(message);
          });
        }
        if (!contentType.includes('application/json')) {
          throw new Error('Expected JSON but got: ' + contentType);
        }
        return response.json();
      })
      .then(function (response) {
        if (response && response.success) {
          loginSucceeded = true;
          clearRearm();
          window.location.href = redirectTarget(response.redirect);
        } else {
          // response can be null here (a literal JSON null body), so read the
          // message defensively — dereferencing it would replace the real
          // failure with a TypeError in the user's alert.
          var reported = response && (response.error || response.message);
          throw new Error(reported || 'Unknown error');
        }
      })
      .catch(function (error) {
        // Clear before onFailure: the conditional flow's onFailure restarts,
        // and its guard reads finishInProgress.
        finishInProgress = false;
        console.error('Passkey: finish login error:', error);
        alert(Drupal.t('Passkey authentication failed: !message', { '!message': error.message }));
        onFailure();
      });
  }

  /**
   * Where to land after login: the destination parameter wins, then the
   * server's own redirect, then the user's account page.
   */
  function redirectTarget(serverRedirect) {
    var destination = new URLSearchParams(window.location.search).get('destination');
    return sameOriginPath(destination) || sameOriginPath(serverRedirect) || '/user';
  }

  /**
   * Resolve a candidate redirect and return it only if it stays on this origin.
   *
   * The destination parameter comes from whatever link the user followed to
   * reach the login page, so it is attacker-controlled and cannot be handed to
   * window.location.href unchecked.
   *
   * Matching dangerous prefixes by hand does not work: browsers normalise
   * backslashes to slashes in http(s) URLs, so "\evil.com" passes a
   * leading-"//" check and then becomes "/\evil.com" once a slash is prefixed
   * — which the URL parser reads as the authority "evil.com". Ask the parser
   * that would perform the navigation instead of trying to predict it, and
   * return the normalised path so no later step can reintroduce an authority.
   */
  function sameOriginPath(candidate) {
    if (!candidate) {
      return null;
    }
    var resolved;
    try {
      // Resolve against the origin, not the current URL: Drupal destinations
      // are root-relative ("user/passkeys"), not relative to /user/login.
      resolved = new URL(candidate, window.location.origin);
    } catch (e) {
      return null;
    }
    // Off-site, or a scheme with no origin of its own such as javascript:,
    // whose origin serialises to "null" and so never matches.
    if (resolved.origin !== window.location.origin) {
      return null;
    }
    // Return the absolute URL, not the path. A pathname can itself begin with
    // "//" — "https://oursite//evil.com" has genuinely our origin, so it
    // passes the check above, but its pathname is "//evil.com", which is a
    // protocol-relative URL once the assignment below reparses it. The
    // absolute form pins the origin that was just verified, so no later parse
    // can reinterpret any part of it as an authority.
    return resolved.href;
  }

  // MARK: - Utility functions

  function base64UrlToArrayBuffer(base64Url) {
    var base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    var padding = base64.length % 4;
    if (padding) {
      base64 += new Array(5 - padding).join('=');
    }
    var binary = atob(base64);
    var bytes = new Uint8Array(binary.length);
    for (var i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes.buffer;
  }

  function arrayBufferToBase64Url(buffer) {
    var binary = '';
    var bytes = new Uint8Array(buffer);
    for (var i = 0; i < bytes.length; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    var base64 = btoa(binary);
    return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
  }

  // Expose the redirect logic to tests/redirect.test.js. `module` is undefined
  // in a browser, so this short-circuits and does nothing there — it does ship
  // in the bundle, since terser cannot resolve a runtime typeof check, but it
  // is a few inert bytes and no script on the login page defines `module`.
  // Worth it: this is the one piece of the file pure enough to test without a
  // DOM, and it is where the security bugs have been.
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
      redirectTarget: redirectTarget,
      sameOriginPath: sameOriginPath
    };
  }
})();
